'use client';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import type { User, Session } from '@supabase/supabase-js';

interface Profile {
  id: string;
  phone: string | null;
  full_name: string;
  username: string | null;
  email: string | null;
  avatar_url: string | null;
  role: 'super_admin' | 'owner' | 'staff' | 'trainer' | 'member';
  is_active: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const supabase = createClient();

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_my_profile')
        .maybeSingle();
      if (!error && data) {
        setProfile(data as Profile);
        return data as Profile;
      }
      if (error) {
        console.error('[AuthContext] Profile fetch error:', error.message);
        // Handle JWT clock skew: token issued at future time
        if (error.message?.toLowerCase().includes('jwt issued at future') ||
            error.message?.toLowerCase().includes('iat') ||
            error.code === 'PGRST301'|| error.message?.toLowerCase().includes('invalid jwt')) {
          // Refresh the session to get a new token
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (!refreshError && refreshData?.session) {
            // Retry profile fetch with refreshed session
            const { data: retryData, error: retryError } = await supabase
              .rpc('get_my_profile')
              .maybeSingle();
            if (!retryError && retryData) {
              setProfile(retryData as Profile);
              return retryData as Profile;
            }
          }
        }
      }
    } catch (err) {
      console.error('[AuthContext] Profile fetch exception:', err);
    }
    return null;
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).then(() => setLoading(false));
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase, fetchProfile]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('[AuthContext] Sign out error:', err);
    }
    setUser(null);
    setSession(null);
    setProfile(null);
    // Clear session storage on logout
    try {
      sessionStorage.clear();
    } catch {
      // sessionStorage unavailable
    }
    // Hard navigate to clear all state
    window.location.href = '/sign-up-login-screen';
  }, [supabase]);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
